<template>
    <div class="d-flex flex-column flex-grow-1">
        <CommonEMMLocationInformationComponent ref="commonDialog"
                                               :platform="_platform"
                                               :mapMode="nMapMode"
                                               :getPositionListFunction="getPositionListFunction"
                                               :getAPIKey="getAPIKey"
                                               :getCollectedDateFunction="getCollectedDate"
                                               @onClickedDispGoogleMap="onClickedDispGoogleMap" 
                                               :columnData="_columns"
                                               :assetsData="_assetsData"
                                               :isEmmWebConsoleEnabled="_isEmmWebConsoleEnabled"
                                               :isShowEmmLocationHistoryInfoPanel="_isShowEmmLocationHistoryInfoPanel"/>
    </div>
</template>

<script lang="ts">
    import { Options, Vue } from 'vue-class-component';
    import { AxiosResponse } from 'axios'
    import { LoadingUtil } from 'lib-vue-v2/util';
    import CommonEMMLocationInformationComponent from '@/views/Mobile/EMMLocation/CommonEMMLocationInformationComponent.vue'
    import { SearchColumnData, SearchColumnType } from '@/models/SearchColumnData';
    import { AssetsData } from '@/models/Hardware/HardwareDetailData';

    // C#から受け取るJSONデータ(index.htmlで受け取っている。)
    declare const agentID: number;
    declare const agentName: string;
    declare const computerName: string;
    declare const divisionName: string;
    declare const platform: string;
    declare const completed: boolean;   // C#の受け取りが完了したかどうか
    declare const frontEndURL: string;
    declare const deviceName: string;
    declare const imei: string;
    declare const phoneNumber: string;
    declare const machineSerial: string;
    declare const columns: Array<SearchColumnData>;
    declare const isEmmWebConsoleEnabled: boolean;

    @Options({
        components: {
            CommonEMMLocationInformationComponent,
        },
    })

    export default class EMMLocationView extends Vue {
        private intervalID = -1;
        private _agentID = 0;       // index.html上の変数はリアクティブでないので、こちらにコピーして利用する
        private _agentName = "";    // index.html上の変数はリアクティブでないので、こちらにコピーして利用する
        private _computerName = ""; // index.html上の変数はリアクティブでないので、こちらにコピーして利用する
        private _divisionName = ""; // index.html上の変数はリアクティブでないので、こちらにコピーして利用する
        private _platform = "";
        private nMapMode = 0;    // 表示モード(0:マップ埋め込み表示, 1:マップURL表示)
        private _columns: Array<SearchColumnData> =
            [
                { columnID: 0, dataType: SearchColumnType.number, text: "端末機No" },
                { columnID: 0x1018, dataType: SearchColumnType.link, text: "端末機名" },
                { columnID: 0x2000, dataType: SearchColumnType.string, text: "部署名" },
                { columnID: 0x4002, dataType: SearchColumnType.string, text: "プラットフォーム" },
                { columnID: 0x0002, dataType: SearchColumnType.string, text: "コンピューター名" },
            ];
        private _assetsData = new AssetsData();     // 端末の資産情報
        private _isEmmWebConsoleEnabled = false;    // MDM専用Web管理コンソールのライセンスが有効か否か
        private _isShowEmmLocationHistoryInfoPanel = true;

        /**
         * マウント後の処理
         */
        public async mounted(): Promise<void> {
            // ローディング表示開始
            LoadingUtil.Start();

            // C#から情報が渡ってきたことを検知するためにポーリングする。
            this.intervalID = setInterval(this.onInterval, 100);
        }

        /**
         * 管理機から設定が染み込むまで少し待つ
         */
        private onInterval(): void {
            if (false == completed) {
                return;
            }

            this._agentID = agentID;
            this._platform = platform;
            this._assetsData.agentID = this._agentID.toString();
            this._assetsData.agentName = agentName;
            this._assetsData.computerName = computerName;
            this._assetsData.divisionName = divisionName;
            this._assetsData.deviceName = deviceName;
            this._assetsData.imei = imei;
            this._assetsData.phoneNumber = phoneNumber;
            this._assetsData.machineSerial = machineSerial;
            if (0 < columns.length) {
                this._columns = columns;
            }
            this._isEmmWebConsoleEnabled = isEmmWebConsoleEnabled;

            // ローディング表示終了
            LoadingUtil.Stop();

            // タイマーを止める。
            clearInterval(this.intervalID);
            this.intervalID = -1;

            const refObj: any = this.$refs;
            refObj.commonDialog.getData();
        }

        /**
         * 位置情報を取得する
         */
        public async getPositionListFunction(isDateRange: boolean, startDateUtc: Date, endDateUtc: Date, isNext: boolean, nextTime: string): Promise<AxiosResponse>
        {
            // C#側で収集サーバーから取得してもらう。
            var sendUrl = frontEndURL + "/mobile/GetPositionListToVue";

            var bodyData = {
                agentID: this._agentID,
                isDateRange: isDateRange,
                startDateUtc: startDateUtc,
                endDateUtc: endDateUtc,
                isNext: isNext,
                nextTime: nextTime
            };

            // 位置情報を取得
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            let encResponse = await window.chrome.webview.hostObjects.LocationInfoJsListner.Post(sendUrl, JSON.stringify(bodyData), "application/json")
            if (null == encResponse || '' == encResponse) {
                const retInternalServerError: any = { status: 500 }
                return retInternalServerError;
            }

            // 取得した位置情報は暗号化されているので復号する
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            let decResponse = await window.chrome.webview.hostObjects.LocationInfoJsListner.DecryptLocation(encResponse);
            if (null == decResponse || '' == decResponse) {
                const retInternalServerError: any = { status: 500 }
                return retInternalServerError;
            }

            const ret: any = { status: 200, data: JSON.parse(decResponse) }
            return ret;
        }

        /**
         * APIKeyを取得する
         */
        public async getAPIKey(): Promise<AxiosResponse> {
            // C#側で収集サーバーから取得してもらう。
            var sendUrl = frontEndURL + "/mobile/GetMapApiKey";

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            let response = await window.chrome.webview.hostObjects.LocationInfoJsListner.Post(sendUrl, "", "application/json")
            if (null == response || '' == response) {
                const retInternalServerError: any = { status: 500 }
                return retInternalServerError;
            }

            var json = JSON.parse(response);
            if (undefined != json.IsAzure && !json.IsAzure) {
                // Azure環境でなければ遷移式
                this.nMapMode = 1;
            }
            else {
                // Azure環境はマップ埋め込み式(取得失敗系もこちら)
                this.nMapMode = 0;
            }

            const ret: any = { status: 200, data: { apiKey: json.ApiKey } }
            return ret;
        }

        /**
         * 位置情報の収集日を取得する
         */
        public async getCollectedDate(): Promise<AxiosResponse> {
            // C#側で収集サーバーから取得してもらう。
            var sendUrl = frontEndURL + "/mobile/GetCollectedPositionDate";

            var bodyData = {
                agentID: this._agentID,
                diffUtcMinute: (new Date().getTimezoneOffset() * -1)
            };

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            let response = await window.chrome.webview.hostObjects.LocationInfoJsListner.Post(sendUrl, JSON.stringify(bodyData), "application/json")
            if (null == response || '' == response) {
                const retInternalServerError: any = { status: 500 }
                return retInternalServerError;
            }
            var json = JSON.parse(response);

            const ret: any = { status: 200, data: json.dateList }
            return ret;
        }

        /**
         * GoogleMapを表示する
         */
        public onClickedDispGoogleMap(url: string): void {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            window.chrome.webview.hostObjects.LocationInfoJsListner.ShowGoogleMap(url);
        }
    }
</script>

<style scoped>
</style>